import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { getFQDNFromString } from '../../../../../../_metronic/helpers/AssetHelpers';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { normalizeVpn, Vpn, vpnLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import UrlWithMetadataCard from '../card/UrlWithMetadataCard';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { AddInOutboundLinksModal } from '../modal/AddInOutboundLinksModal';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, SiRequestUser } from '../utils';

interface Props {
  data: any;
  reviewer?: SiRequestUser[];
  status: string;
  refetch: () => void;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSave?: UsePageDataManagerResult['saveService'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

type LinkedSite = { index?: number; _id?: string; url: string };
type UrlObj = {
  title: string;
  description: string;
  linkedSites: LinkedSite[];
};
type UrlMap = Map<string, UrlObj>;
type GroupedDetails = Map<
  Vpn,
  {
    preview: UrlMap; // one link from each of the first 5 FQDNs
    others: UrlMap; // all other links
  }
>;

export default function OutboundFlowDetail({
  data,
  reviewer,
  status,
  refetch,
  isEditable,
  isSaving,
  onUpdate,
  onSave,
  onReset,
  checkDirty,
}: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_OUTBOUND_FLOW_DETAIL.${id}` }, values);
  const { setToast } = useToast();
  const [showVpnOtherLinks, setShowVpnOtherLinks] = useState<Set<Vpn>>(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const maxPreviewLinksPerVpn = 5;
  const emDash = '—';
  const totalFoundLinksFieldUpdates = getFieldUpdate('result.total_found_links', data?.field_updates);
  const outboundFqdnCountFieldUpdates = getFieldUpdate('result.fqdn_group.count', data?.field_updates);
  const outboundFqdnFieldUpdates = getFieldUpdate('result.fqdn_group.fqdn', data?.field_updates);
  const hasHistory = [
    totalFoundLinksFieldUpdates,
    outboundFqdnCountFieldUpdates,
    outboundFqdnFieldUpdates,
  ].some((fieldUpdates) => fieldUpdates.length > 0);
  const getReviewerEmail = (userId?: string) =>
    reviewer?.find((reviewerData) => reviewerData.user_id === userId)?.email ?? userId;
  const fqdnGroupReviewerNames = Array.from(
    new Set(
      [...outboundFqdnCountFieldUpdates, ...outboundFqdnFieldUpdates]
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );
  const totalFoundLinksReviewerNames = Array.from(
    new Set(
      totalFoundLinksFieldUpdates
        .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
        .filter(Boolean)
    )
  );

  const groupedDetails = useMemo(() => {
    const map: GroupedDetails = new Map();
    const previewVpnFqdnSet = new Set<string>(); // format: normalizedVpn|fqdn

    if (!data?.detail?.length) return map;

    // Sort by length of title and description
    const sortedDetails = data.detail.toSorted(
      (a, b) =>
        (b?.title?.length || 0) +
        (b?.description?.length || 0) -
        ((a?.title?.length || 0) + (a?.description?.length || 0))
    );

    sortedDetails.forEach((item, idx) => {
      const normalizedVpn = normalizeVpn(item?.vpn);
      if (!map.has(normalizedVpn)) {
        map.set(normalizedVpn, { preview: new Map(), others: new Map() });
      }
      const vpnObject = map.get(normalizedVpn);
      const fqdn = getFQDNFromString(item?.found_link);
      const isLinkInPreview = vpnObject.preview.has(item?.found_link);
      const isLinkInOthers = vpnObject.others.has(item?.found_link);
      const linkedSiteObj: UrlObj['linkedSites'][number] = {
        index: idx,
        _id: item?._id,
        url: item?.source_link,
      };

      if (isLinkInPreview || isLinkInOthers) {
        const existingUrlObj = vpnObject[isLinkInPreview ? 'preview' : 'others'].get(
          item?.found_link
        );
        if (!existingUrlObj.linkedSites.some((site) => site.url === item?.source_link)) {
          existingUrlObj.linkedSites.push(linkedSiteObj);
        }
      } else {
        const isPreviewMaxed = vpnObject.preview.size >= maxPreviewLinksPerVpn;
        const isFqdnInPreview = !isEditable && previewVpnFqdnSet.has(`${normalizedVpn}|${fqdn}`);
        const urlObject: UrlObj = {
          title: item?.title,
          description: item?.description,
          linkedSites: [linkedSiteObj],
        };
        if (isPreviewMaxed || isFqdnInPreview) {
          vpnObject.others.set(item?.found_link, urlObject);
        } else {
          vpnObject.preview.set(item?.found_link, urlObject);
          previewVpnFqdnSet.add(`${normalizedVpn}|${fqdn}`);
        }
      }
    });
    return map;
  }, [data]);

  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };

  const dataSection: SummaryRow[] = [
    {
      key: 'fqdnGroup',
      field: t('OUTBOUND_FQDN', { count: data?.result?.fqdn_group?.count?.toLocaleString() ?? 0 }),
      reviewer:
        (fqdnGroupReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {fqdnGroupReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => (
        <>
          {data?.result?.fqdn_group?.fqdn?.length > 0 ? (
            <UnlabeledBulletColumns
              data={data.result.fqdn_group.fqdn}
              columnCount={data.result.fqdn_group.fqdn.length > 2 ? 2 : 1}
              shouldBreakText
            />
          ) : (
            emDash
          )}
        </>
      ),
    },
    {
      key: 'totalFoundLinks',
      field: t('FOUND_LINKS'),
      reviewer:
        (totalFoundLinksReviewerNames.length > 0 && isEditable) ? (
          <div className='d-flex align-items-center gap-1 flex-wrap'>
            {totalFoundLinksReviewerNames.map((reviewerName) => (
              <Avatar key={reviewerName} name={reviewerName} />
            ))}
          </div>
        ) : undefined,
      render: () => (
        <>
          {data?.result?.total_found_links
            ? t('ABOUT_RESULTS', { count: data.result.total_found_links.toLocaleString() })
            : emDash}
        </>
      ),
    },
  ];

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          isEditable && (
            <div
              className='btn btn-icon btn-sm btn-primary'
              onClick={handleOpenAddModal}
              title='Add Outbound Links'
            >
              <i className='fas fa-plus'></i>
            </div>
          )
        }
        isEditable={false}
      >
        <div className='p-9 vstack gap-9'>
          <SummaryTable
            rows={dataSection}
            data={data}
            refetch={refetch}
            className='mb-0'
            title={<h5 className='fw-bolder m-0'>{t('SUMMARY')}</h5>}
          />

          {Object.keys(vpnLabel).filter((area: Vpn) => area === 'ASIA').map((area: Vpn) => {
            const groupedData = groupedDetails.get(area);
            const showOtherLinks = showVpnOtherLinks.has(area);
            return (
              <div key={area}>
                <h5 className='fw-bolder'>{t('INSPECTED_RESULTS')}</h5>

                {groupedData && groupedData.preview.size > 0 ? (
                  <div className='vstack gap-4'>
                    {Array.from(groupedData.preview, ([link, value]) => (
                      <div key={link}>
                        <UrlWithMetadataCard
                          url={link}
                          title={value?.title}
                          description={value?.description}
                          linkedSites={value?.linkedSites?.map((site) => site.url)}
                        />
                      </div>
                    ))}

                    {showOtherLinks &&
                      Array.from(groupedData.others, ([link, value], index) => (
                        <div key={index}>
                          <UrlWithMetadataCard
                            url={link}
                            title={value?.title}
                            description={value?.description}
                            linkedSites={value?.linkedSites?.map((site) => site.url)}
                          />
                        </div>
                      ))}
                  </div>
                ) : (
                  <div>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })}</div>
                )}

                {groupedData && groupedData.others.size > 0 && (
                  <div className='mt-4'>
                    <button
                      className='btn btn-link btn-color-muted btn-active-color-primary btn-sm p-0'
                      onClick={(e) => {
                        e.currentTarget.blur();
                        setShowVpnOtherLinks((prev) => {
                          const newSet = new Set(prev);
                          if (showOtherLinks) newSet.delete(area);
                          else newSet.add(area);
                          return newSet;
                        });
                      }}
                    >
                      {showOtherLinks ? (
                        <>
                          {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                        </>
                      ) : (
                        <>
                          {t('SHOW_ALL_MORE', { remaining: groupedData.others.size })}
                          <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      {isEditable && (
        <AddInOutboundLinksModal
          data={data}
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          handleUpdate={onUpdate}
          handleSave={onSave}
        />
      )}

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?._id,
            paths: ['result.total_found_links', 'result.fqdn_group.count', 'result.fqdn_group.fqdn'],
          },
        ]}
      />
    </>
  );
}
